import { Container, Title, Text, Stack, Divider, List, Anchor } from '@mantine/core';

export default function SMSTerms() {
  return (
    <Container size="md" py="xl">
      <Stack gap="lg">
        <Title order={1}>SMS Terms of Service</Title>
        <Text c="dimmed" size="sm">Last updated: April 11, 2026</Text>

        <Divider />

        <Title order={3}>Program Description</Title>
        <Text>
          Standard Produce Company offers an SMS messaging service for existing B2B
          customers. When you opt in, you will receive customer care messages including:
        </Text>
        <List>
          <List.Item>Order confirmations and updates</List.Item>
          <List.Item>Delivery status notifications and ETA alerts</List.Item>
          <List.Item>Account balance and invoice notifications</List.Item>
          <List.Item>Responses to your SMS commands (ordering, balance inquiries, payments)</List.Item>
        </List>

        <Title order={3}>How to Opt In</Title>
        <Text>
          You can opt in to receive SMS messages from Standard Produce by any of the
          following verifiable methods:
        </Text>
        <List type="ordered">
          <List.Item>
            <Text fw={600}>Text START</Text> — Send the keyword START (or SUBSCRIBE,
            JOIN, OPTIN) to our business number (434) 204-2184 to subscribe. You will
            receive a confirmation message with program details, frequency disclosure,
            and HELP/STOP instructions.
          </List.Item>
          <List.Item>
            <Text fw={600}>Customer portal (web form)</Text> — Log in to your
            authenticated account at portal.standardproduce.com and navigate to
            Account &gt; SMS Preferences. Check the consent acknowledgment box (which
            links to these SMS Terms and our Privacy Policy and displays message
            frequency and "message and data rates may apply" disclosures) and enable
            the SMS notifications toggle. Your consent is recorded with timestamp,
            IP address, and the full text of the acknowledgment you accepted. A
            publicly viewable preview of this opt-in screen is available at{' '}
            <Anchor href="/sms-opt-in-preview" inherit>portal.standardproduce.com/sms-opt-in-preview</Anchor>.
          </List.Item>
          <List.Item>
            <Text fw={600}>Written consent at onboarding</Text> — During B2B account
            onboarding, a Standard Produce sales representative will present a written
            SMS consent form that includes program description, message frequency,
            "message and data rates may apply," HELP/STOP instructions, and links to
            these SMS Terms and our Privacy Policy. The signed form is retained in
            our CRM and linked to your customer account.
          </List.Item>
        </List>
        <Text>
          By opting in through any of the methods above, you consent to receive recurring
          automated text messages from Standard Produce at the mobile number you provide.
          Consent is not a condition of purchase. Only existing business customers with
          active accounts are eligible to opt in.
        </Text>

        <Title order={3}>Message Frequency</Title>
        <Text>
          Message frequency varies based on your ordering activity. Typically 2-10
          messages per week for active accounts. You will receive messages only in
          response to your orders, deliveries, and account activity.
        </Text>

        <Title order={3}>How to Opt Out</Title>
        <Text>
          You can opt out at any time by texting <strong>STOP</strong> to our business
          number. You may also text CANCEL, QUIT, END, or UNSUBSCRIBE. You will receive
          a single confirmation message and no further messages will be sent. To
          resubscribe, text START.
        </Text>

        <Title order={3}>Help</Title>
        <Text>
          For help, text <strong>HELP</strong> to our business number, or contact us
          directly at (434) 204-2184 or info@standardproduce.com.
        </Text>

        <Title order={3}>Message and Data Rates</Title>
        <Text>
          Standard message and data rates may apply. Check with your mobile carrier
          for details about your messaging plan.
        </Text>

        <Title order={3}>Supported Carriers</Title>
        <Text>
          Our SMS service is supported on all major U.S. carriers including AT&T,
          Verizon, T-Mobile, Sprint, and most regional carriers.
        </Text>

        <Title order={3}>Privacy</Title>
        <Text>
          No mobile information, including phone numbers and SMS opt-in data, will be
          shared with third parties or affiliates for marketing or promotional purposes.
          This category of personal information is never shared for marketing. Your
          information is used solely to provide our SMS ordering and notification service.
          See our full{' '}
          <Anchor href="/privacy" inherit>Privacy Policy</Anchor> and general{' '}
          <Anchor href="/terms" inherit>Terms of Service</Anchor> for details.
        </Text>

        <Title order={3}>Contact</Title>
        <Text>
          Standard Produce Company{'\n'}
          Charlottesville, VA{'\n'}
          Phone: (434) 204-2184{'\n'}
          Email: info@standardproduce.com
        </Text>
      </Stack>
    </Container>
  );
}
